import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Chip, Tabs, Tab } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { withSerialNo } from '../../components/ui/gridColumns';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import { opsEntityPath } from '../../utils/opsEntityNav';
import { getPaymentTransactions } from '../../services/business/paymentTransactionService';

const METHODS = [
  { key: 'all', label: 'All' },
  { key: 'cash', label: 'Cash' },
  { key: 'wallet', label: 'Wallet' },
  { key: 'card', label: 'Card / Online' },
] as const;

const BUCKET_LABEL: Record<string, string> = {
  cash: 'Cash',
  wallet: 'Wallet',
  card: 'Card / Online',
};

export default function PaymentTransactionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatAmount } = useTenantCurrency();
  const method = searchParams.get('method') || 'all';
  const from = searchParams.get('from') || undefined;
  const to = searchParams.get('to') || undefined;
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['payment-transactions', method, from, to, page],
    queryFn: () => getPaymentTransactions({
      method: method === 'all' ? undefined : method,
      from,
      to,
      page: page + 1,
      limit: pageSize,
    }),
  });

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 170,
      valueFormatter: (v) => (v ? new Date(v as string).toLocaleString() : '—'),
    },
    { field: 'service', headerName: 'Service', width: 130 },
    { field: 'reference', headerName: 'Reference', width: 160 },
    {
      field: 'paymentBucket',
      headerName: 'Payment',
      width: 140,
      renderCell: (params) => (
        <Chip size="small" label={BUCKET_LABEL[params.value as string] || params.row.paymentMethod} />
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      valueFormatter: (v, row) => formatAmount(Number(v || 0), row),
    },
    { field: 'status', headerName: 'Status', width: 130 },
  ], [formatAmount]);

  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance"
        title="Payment transactions"
        subtitle="Cash, wallet, and card/online payments across transport, delivery, and services."
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Payment transactions' },
        ]}
      />

      <Tabs
        value={METHODS.some((m) => m.key === method) ? method : 'all'}
        onChange={(_, v) => {
          const next = new URLSearchParams(searchParams);
          if (v === 'all') next.delete('method');
          else next.set('method', v);
          setSearchParams(next);
          setPage(0);
        }}
        sx={{ mb: 2 }}
      >
        {METHODS.map((m) => <Tab key={m.key} label={m.label} value={m.key} />)}
      </Tabs>

      <DataGrid
        rows={data?.data || []}
        columns={withSerialNo(columns)}
        loading={isLoading}
        getRowId={(r) => r._id}
        autoHeight
        paginationMode="server"
        rowCount={data?.meta?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m) => setPage(m.page)}
        pageSizeOptions={[pageSize]}
        onRowClick={(params) => {
          const path = opsEntityPath(params.row as unknown as Record<string, unknown>);
          if (path) navigate(path, { state: { from: '/payment-transactions' } });
        }}
        sx={{ bgcolor: 'background.paper', borderRadius: 2, cursor: 'pointer' }}
      />
    </PageShell>
  );
}
